import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
// form
import { Controller, useFormContext } from 'react-hook-form';
// @mui
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Scrollbar from '../scrollbar/Scrollbar';

// ----------------------------------------------------------------------

RHFSelect.propTypes = {
  name: PropTypes.string,
  native: PropTypes.bool,
  children: PropTypes.node,
  helperText: PropTypes.node,
  maxHeight: PropTypes.number,
  limitOptionsWidth: PropTypes.bool,
  SelectProps: PropTypes.object,
};

const generatePaperSx = ({ limitOptionsWidth, selectWidth }) => {
  if (!limitOptionsWidth) return {};

  return {
    minWidth: 0,
    maxWidth: selectWidth,
  };
};

export function RHFSelect({
  name,
  native,
  children,
  helperText,
  maxHeight = 220,
  limitOptionsWidth,
  SelectProps: externalSelectProps,
  ...other
}) {
  const { control } = useFormContext();

  const rootRef = useRef();
  const inputRef = useRef();
  const [selectWidth, setSelectWidth] = useState(inputRef?.current?.node?.offsetWidth);
  const paperSxProps = generatePaperSx({ limitOptionsWidth, selectWidth });

  useEffect(() => {
    if (other?.focus) {
      inputRef.current.focus();
    }
  }, [other?.focus]);

  useEffect(() => {
    if (!limitOptionsWidth || !rootRef.current) return;

    const element = rootRef.current;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setSelectWidth(entry.contentRect.width);
      }
    });

    observer.observe(element);

    setSelectWidth(element.offsetWidth);

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [limitOptionsWidth]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          ref={rootRef}
          inputRef={inputRef}
          select
          fullWidth
          SelectProps={{
            native,
            MenuProps: {
              PaperProps: {
                sx: {
                  ...(!native && {
                    ...paperSxProps,
                    px: 1,
                    maxHeight: typeof maxHeight === 'number' ? maxHeight : 'unset',
                    '& .MuiMenuItem-root': {
                      px: 1,
                      borderRadius: 0.75,
                      typography: 'body2',
                    },
                  }),
                },
              },
            },
            ...externalSelectProps,
          }}
          error={!!error}
          helperText={error ? error?.message : helperText}
          {...other}
          onChange={(e) => {
            if (other?.onChange) {
              other.onChange(e);
            }

            field.onChange(e);
          }}
        >
          {children}
        </TextField>
      )}
    />
  );
}

const TruncatedText = ({ text, maxWidth, checkbox }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text]);

  const typography = (
    <Typography
      ref={textRef}
      sx={{
        maxWidth: maxWidth || (checkbox ? 200 : 250),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
      }}
    >
      {text}
    </Typography>
  );

  if (isTruncated) {
    return (
      <Tooltip title={text} arrow>
        {typography}
      </Tooltip>
    );
  }

  return typography;
};

TruncatedText.propTypes = {
  text: PropTypes.string.isRequired,
  maxWidth: PropTypes.number,
  checkbox: PropTypes.bool,
};

const TruncatedChip = ({ text, maxWidth = 120 }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text]);

  const chipContent = (
    <Typography
      ref={textRef}
      sx={{
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </Typography>
  );

  if (isTruncated) {
    return (
      <Tooltip title={text} arrow>
        <Chip size="small" label={chipContent} />
      </Tooltip>
    );
  }

  return <Chip size="small" label={chipContent} />;
};

TruncatedChip.propTypes = {
  text: PropTypes.string.isRequired,
  maxWidth: PropTypes.number,
};

// ----------------------------------------------------------------------

RHFMultiSelect.propTypes = {
  name: PropTypes.string,
  chip: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.array,
  checkbox: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  sx: PropTypes.object,
};

export function RHFMultiSelect({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  helperText,
  sx,
  ...other
}) {
  const { control } = useFormContext();

  const renderValues = (selectedIds) => {
    const selectedItems = options.filter((item) => selectedIds.includes(item.value));

    if (!selectedItems.length && placeholder) {
      return (
        <Box component="em" sx={{ color: 'text.disabled' }}>
          {placeholder}
        </Box>
      );
    }

    if (chip) {
      return (
        <Scrollbar>
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5 }}>
            {selectedItems.map((item) => (
              <TruncatedChip key={item.value} text={item.label} />
            ))}
          </Box>
        </Scrollbar>
      );
    }

    return selectedItems.map((item) => item.label).join(', ');
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={sx}>
          {label && (
            <InputLabel id={name} size={other.size ?? 'normal'}>
              {label}
            </InputLabel>
          )}

          <Select
            {...field}
            multiple
            displayEmpty={!!placeholder}
            labelId={name}
            input={<OutlinedInput fullWidth label={label} error={!!error} />}
            renderValue={renderValues}
            MenuProps={{
              PaperProps: {
                sx: { px: 1, maxHeight: 280 },
              },
            }}
            {...other}
          >
            {placeholder && (
              <MenuItem
                disabled
                value=""
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: 'body2',
                }}
              >
                <em> {placeholder} </em>
              </MenuItem>
            )}

            {options.map((option) => {
              const selected = field?.value?.includes(option.value);

              return (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 0.75,
                    typography: 'body2',
                    ...(selected && {
                      fontWeight: 'fontWeightMedium',
                    }),
                    ...(checkbox && {
                      p: 0.25,
                    }),
                  }}
                >
                  {checkbox && <Checkbox disableRipple size="small" checked={selected} />}

                  <TruncatedText text={option.label} checkbox={checkbox} />
                </MenuItem>
              );
            })}

            {options.length === 0 && (
              <MenuItem disabled sx={{ py: 1, px: 2, borderRadius: 0.75, typography: 'body2' }}>
                Nenhum registro encontrado
              </MenuItem>
            )}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
